import { ReactNode, useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogContent, DialogTitle, Grid, MenuItem, TextField, Typography } from '@mui/material';
import { Add, AddBox, Apartment, BookmarkAdd, IndeterminateCheckBox } from '@mui/icons-material';
import ControlledDatePicker from 'src/shared/components/form/controlledDatePicker';
import { useTranslation } from 'react-i18next';
import { RecruitText } from 'src/shared/models/enums/recruitText.enum';
import { MemberType } from 'src/shared/models/invoiceMission/invoiceMission';
import { Controller, useFormContext } from 'react-hook-form';
import { Associations, ProfileItemResponse } from 'src/modules/invoice/models/invoiceTemplate';
import { ProfileFilterPicker } from 'src/shared/components/organisms/profileFilterPicker';
import dayjs from 'dayjs';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getAllOrganizations } from 'src/shared/services/organizationService';
import { useGlobals } from 'src/shared/contexts/appContext';
import { getPermission } from 'src/shared/functions/getSysProductPermissions';
import { PermissionEnum } from 'src/shared/models/enums/permissionEnums';
import { useInvoiceTemplateType } from '../hooks/useInvoiceTemplateType';
import { InvoiceTemplateAddAssociations } from './invoiceTemplateAddAssociations';
import { InvoiceTemplateSelectionPicker } from './invoiceTemplateSelectionPicker';
import { ListDisplay } from './listDisplay';

// Profillistorna som går att redigera i mallen.
type ProfileListField = 'profilesIncludeItems' | 'profilesExcludeItems' | 'profilesMarkItems';

type SelectionWithProfilesSectionProps = {
  readOnly?: boolean;
  // Avgjort av useInvoiceTemplateSelection, som också matar sammanfattningen bredvid.
  showAssociations: boolean;
};

export const SelectionWithProfilesSection = ({ readOnly = false, showAssociations }: SelectionWithProfilesSectionProps) => {
  const { t } = useTranslation();
  const { control, setValue, watch } = useFormContext();
  const { usesRecurringSelection } = useInvoiceTemplateType();
  const isMounted = useIsMounted();
  const { currentUser } = useGlobals();

  // Värvad och uträdesdatum sätter vi åt kunden när en mall behöver dem. De döljs därför, som
  // mallens övriga interna inställningar, i stället för att stå där och se valbara ut.
  const canSeeInternalFields = getPermission(currentUser, PermissionEnum.InvoiceCreateForCustomer);

  // Snabbvalen sätter datumen till förbundets förfallodatum (MMdd) för ett helt medlemsår, samma
  // datum som utskicksbeställningen räknar medlemsår från. Saknar förbundet ett sådant datum finns
  // inget att räkna på, och då visas inga snabbval.
  const [expireMMdd, setExpireMMdd] = useState<string | null>(null);

  useEffect(() => {
    const fetchOrganization = async () => {
      try {
        const result = await getAllOrganizations();
        const organization = result.data.results?.[0];
        if (!isMounted() || !organization) return;
        const raw = (organization.expireDate ?? '').trim();
        setExpireMMdd(/^\d{4}$/.test(raw) ? raw : null);
      } catch (err) {
        if (isMounted()) ConsoleHelper.log(err);
      }
    };

    fetchOrganization();
  }, [isMounted]);

  const currentYear = dayjs().year();
  const quickYears = [currentYear, currentYear + 1];

  // Medlemsåret är det år aviseringen gäller, som i utskicksbeställningen. De medlemmar som ska
  // förnya till det året är de vars nuvarande medlemskap går ut året innan, så datumen sätts där.
  const setMembershipYear = (year: number) => {
    if (!expireMMdd) return;
    const date = dayjs(`${year - 1}-${expireMMdd.slice(0, 2)}-${expireMMdd.slice(2, 4)}`);
    if (!date.isValid()) return;
    setValue('expireDateFrom', date, { shouldDirty: true });
    setValue('expireDateTom', date, { shouldDirty: true });
  };

  const clearExpireDates = () => {
    setValue('expireDateFrom', '', { shouldDirty: true });
    setValue('expireDateTom', '', { shouldDirty: true });
  };

  // Datumen ligger antingen som Dayjs (nyss valt) eller som YYYYMMDD (hämtat från mallen).
  const toDate = (value: unknown) => {
    if (!value) return null;
    const parsed = dayjs.isDayjs(value) ? value : dayjs(String(value), 'YYYYMMDD');
    return parsed.isValid() ? parsed : null;
  };

  // Basåret är det år medlemskapen går ut, alltså slutdatumets år. Chipsen nedan flyttar bara
  // startdatumet bakåt, så att obetalda från tidigare år kommer med i samma avisering.
  const expireTomDate = toDate(watch('expireDateTom'));
  const expireFromDate = toDate(watch('expireDateFrom'));
  const baseYear = expireTomDate ? expireTomDate.year() : null;

  const yearsBackOptions: (number | null)[] = [0, 1, 2, 3, null];

  const dateForYearsBack = (yearsBack: number) =>
    expireMMdd && baseYear != null ? dayjs(`${baseYear - yearsBack}-${expireMMdd.slice(0, 2)}-${expireMMdd.slice(2, 4)}`) : null;

  const setYearsBack = (yearsBack: number | null) => {
    if (yearsBack === null) {
      setValue('expireDateFrom', '', { shouldDirty: true });
      return;
    }

    const date = dateForYearsBack(yearsBack);
    if (date && date.isValid()) setValue('expireDateFrom', date, { shouldDirty: true });
  };

  // Slutdatumet är förfallodatumet året före medlemsåret, så det visar vilket medlemsår som gäller.
  const isMembershipYearSelected = (year: number) => {
    if (!expireMMdd || !expireTomDate) return false;
    const date = dayjs(`${year - 1}-${expireMMdd.slice(0, 2)}-${expireMMdd.slice(2, 4)}`);
    return date.isValid() && expireTomDate.isSame(date, 'day');
  };

  const isYearsBackSelected = (yearsBack: number | null) => {
    if (yearsBack === null) return !expireFromDate;
    const date = dateForYearsBack(yearsBack);
    return !!expireFromDate && !!date && expireFromDate.isSame(date, 'day');
  };
  // Vilken lista modalen "Lägg till" lägger profiler i (null = stängd).

  const [showAddAssociations, setShowAddAssociations] = useState(false);
  const associations: Associations[] = watch('associations') || [];
  const profilesMarkItems: ProfileItemResponse[] = watch('profilesMarkItems') || [];
  const profilesIncludeItems: ProfileItemResponse[] = watch('profilesIncludeItems') || [];
  const profilesExcludeItems: ProfileItemResponse[] = watch('profilesExcludeItems') || [];

  const getRecruitedText = (key: RecruitText) => {
    const recruitTexts: Record<RecruitText, string> = {
      [RecruitText.Recruit0]: 'invoice:adminInvoiceTemplatePage.recruit.recruit0',
      [RecruitText.Recruit1]: 'invoice:adminInvoiceTemplatePage.recruit.recruit1',
      [RecruitText.Recruit2]: 'invoice:adminInvoiceTemplatePage.recruit.recruit2',
    };

    return t(recruitTexts[key] || 'invoice:adminInvoiceTemplatePage.recruit.unknown');
  };

  const getMemberTypeText = (key: MemberType) => {
    switch (key) {
      case MemberType.Active:
        return t('invoice:adminInvoiceTemplatePage.memberType.active');
      case MemberType.All:
        return t('invoice:adminInvoiceTemplatePage.memberType.all');
      case MemberType.Interested:
        return t('invoice:adminInvoiceTemplatePage.memberType.interested');
      case MemberType.Other:
        return t('invoice:adminInvoiceTemplatePage.memberType.other');
      case MemberType.Resigned:
        return t('invoice:adminInvoiceTemplatePage.memberType.resigned');
      case MemberType.Unknown:
        return t('invoice:adminInvoiceTemplatePage.memberType.unknown');
      default:
        return '';
    }
  };

  // Profillistorna går att redigera; profilesInclude/profilesExclude/profilesMark-strängarna byggs från listorna när mallen sparas.
  const profileLists: Record<ProfileListField, ProfileItemResponse[]> = {
    profilesIncludeItems,
    profilesExcludeItems,
    profilesMarkItems,
  };
  const getProfiles = (field: ProfileListField) => profileLists[field];
  const setProfiles = (field: ProfileListField, items: ProfileItemResponse[]) => setValue(field, items, { shouldDirty: true });

  // Föreningarna går att redigera; selection-strängen (förenings-id:n) byggs från listan när mallen sparas. Tom lista = samtliga föreningar.
  const setAssociations = (items: Associations[]) => setValue('associations', items, { shouldDirty: true });

  const handleAssociationsAdded = (added: Associations[]) => {
    setAssociations([...associations, ...added.filter((a) => !associations.some((existing) => existing.id === a.id))]);
    setShowAddAssociations(false);
  };

  const handleAssociationDeleted = (item: Associations) => setAssociations(associations.filter((a) => a.id !== item.id));

  const renderAddButton = (onClick: () => void) =>
    !readOnly && (
      <Box sx={{ mt: 1 }}>
        <Button variant='outlined' size='small' endIcon={<Add fontSize='small' />} onClick={onClick}>
          {t('common:button.add')}
        </Button>
      </Box>
    );

  const profileListTitleKeys: Record<ProfileListField, string> = {
    profilesIncludeItems: 'invoice:adminInvoiceTemplatePage.templateInformation.includedProfiles',
    profilesExcludeItems: 'invoice:adminInvoiceTemplatePage.templateInformation.excludedProfiles',
    profilesMarkItems: 'invoice:adminInvoiceTemplatePage.templateInformation.taggedWithProfiles',
  };

  // Profilvillkoren väljs med samma komponent som i utskicksbeställningens urvalssteg: chips för det
  // valda och ett formulär med profilgrupp och profil.
  const renderEditableProfileList = (field: ProfileListField, icon: ReactNode, mt = 3) => (
    <Grid item xs={12} mt={mt}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>{icon}</Box>
      <ProfileFilterPicker
        title={t(profileListTitleKeys[field])}
        values={getProfiles(field)}
        onChange={(values) => setProfiles(field, values)}
        readOnly={readOnly}
        emptyText={t('invoice:adminInvoiceTemplatePage.templateInformation.noneSelected')}
      />
    </Grid>
  );

  const memberTypeOptions = [MemberType.Unknown, MemberType.Active, MemberType.Interested, MemberType.Other, MemberType.All, MemberType.Resigned];

  return (
    <Grid container>
      <Grid container mb={1}>
        <Grid item xs={12}>
          <Box sx={{ mb: 2 }}>
            {/* Mallen är ett urval, inte ett utskick. Det är inte självklart för den som fyller i den. */}
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
              {t('invoice:adminInvoiceTemplatePage.templateInformation.selectionIntro')}
            </Typography>

            {!usesRecurringSelection && !!expireMMdd && (
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {quickYears.map((year) => (
                  <Button
                    key={year}
                    size='small'
                    disableElevation
                    variant={isMembershipYearSelected(year) ? 'contained' : 'outlined'}
                    disabled={readOnly}
                    onClick={() => setMembershipYear(year)}
                  >
                    {t('invoice:adminInvoiceTemplatePage.templateInformation.quickMembershipYear', { year })}
                  </Button>
                ))}
                <Button size='small' color='inherit' disabled={readOnly} onClick={clearExpireDates}>
                  {t('invoice:adminInvoiceTemplatePage.templateInformation.quickClearDates')}
                </Button>
                <Typography variant='caption' color='text.secondary'>
                  {t('invoice:adminInvoiceTemplatePage.templateInformation.quickHelper', {
                    date: `${expireMMdd.slice(0, 2)}-${expireMMdd.slice(2, 4)}`,
                  })}
                </Typography>
              </Box>
            )}

            {/* Samma val som i utskicksbeställningen: hur långt bakåt obetalda medlemmar ska tas med. */}
            {!usesRecurringSelection && !!expireMMdd && baseYear != null && (
              <Box sx={{ mb: 2 }}>
                <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                  {t('invoice:adminInvoiceTemplatePage.templateInformation.unpaidBackLabel')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                  {yearsBackOptions.map((yearsBack) => {
                    const selected = isYearsBackSelected(yearsBack);

                    let label = t('invoice:adminInvoiceTemplatePage.templateInformation.unpaidBackAll');
                    if (yearsBack === 0) label = t('invoice:adminInvoiceTemplatePage.templateInformation.unpaidBackNo');
                    else if (yearsBack !== null)
                      label = t('invoice:adminInvoiceTemplatePage.templateInformation.unpaidBackFrom', { year: baseYear - yearsBack });

                    return (
                      <Button
                        key={String(yearsBack)}
                        size='small'
                        disableElevation
                        variant={selected ? 'contained' : 'outlined'}
                        disabled={readOnly}
                        onClick={() => setYearsBack(yearsBack)}
                      >
                        {label}
                      </Button>
                    );
                  })}
                </Box>
              </Box>
            )}

            <Grid container spacing={3} justifyContent='space-between' mb={3}>
              {/*
                Autogiro- och swishkörningen plockar själv ut dem som har ett avtal och är i tur, så ett
                sparat urval kan bara ta bort medlemmar som annars skulle dragits, utan att någon ser det.
                En mall som redan har ett satt visar det ändå, annars går det inte att se eller ta bort.
              */}
              {(!usesRecurringSelection || !!watch('selection_id')) && (
                <Grid item xs={12}>
                  <InvoiceTemplateSelectionPicker readOnly={readOnly} />
                </Grid>
              )}
              {/*
                Fälten ligger i en rad om fyra. Datumen och Värvad läses inte av swishurvalet, och när de
                döljs packas de kvarvarande fälten bredvid varandra av sig själva.
              */}
              <Grid item xs={12}>
                <Grid container spacing={3}>
                  {!usesRecurringSelection && (
                    <Grid item xs={12} sm={3}>
                      <ControlledDatePicker disabled={readOnly} name='expireDateFrom' label={t('invoice:adminInvoiceTemplatePage.expireDateFrom')} />
                    </Grid>
                  )}
                  {!usesRecurringSelection && (
                    <Grid item xs={12} sm={3}>
                      <ControlledDatePicker disabled={readOnly} name='expireDateTom' label={t('invoice:adminInvoiceTemplatePage.expireDateTom')} />
                    </Grid>
                  )}
                  {canSeeInternalFields && (
                    <Grid item xs={12} sm={3}>
                      <Controller
                        name='newspaper'
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            // Select kan inte ha null som värde – "Oavsett tidning" (null) representeras av ''.
                            value={field.value ?? ''}
                            onChange={(e) => field.onChange(e.target.value === '' ? null : Number(e.target.value))}
                            select
                            disabled={readOnly}
                            fullWidth
                            size='small'
                            sx={{ backgroundColor: 'white' }}
                            label={t('invoice:adminInvoiceTemplatePage.templateInformation.newspaper')}
                            SelectProps={{ displayEmpty: true }}
                            InputLabelProps={{ shrink: true }}
                          >
                            <MenuItem value=''>{t('invoice:adminInvoiceTemplatePage.templateInformation.newspaperAny')}</MenuItem>
                            <MenuItem value={1}>{t('invoice:adminInvoiceTemplatePage.templateInformation.newspaperYes')}</MenuItem>
                            <MenuItem value={0}>{t('invoice:adminInvoiceTemplatePage.templateInformation.newspaperNo')}</MenuItem>
                          </TextField>
                        )}
                      />
                    </Grid>
                  )}
                  {!usesRecurringSelection && canSeeInternalFields && (
                    <Grid item xs={12} sm={3}>
                      <Controller
                        name='recruit'
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            value={field.value ?? ''}
                            select
                            disabled={readOnly}
                            fullWidth
                            size='small'
                            sx={{ backgroundColor: 'white' }}
                            label={t('invoice:adminInvoiceTemplatePage.recruit.recruited')}
                          >
                            {[RecruitText.Recruit0, RecruitText.Recruit1, RecruitText.Recruit2].map((recruit) => (
                              <MenuItem key={recruit} value={recruit}>
                                {getRecruitedText(recruit)}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                      />
                    </Grid>
                  )}
                  {!usesRecurringSelection && canSeeInternalFields && (
                    <Grid item xs={12} sm={3}>
                      <ControlledDatePicker
                        disabled={readOnly}
                        name='payedDateFrom'
                        label={t('invoice:adminInvoiceTemplatePage.templateInformation.payedDateFrom')}
                      />
                    </Grid>
                  )}
                  {!usesRecurringSelection && canSeeInternalFields && (
                    <Grid item xs={12} sm={3}>
                      <ControlledDatePicker
                        disabled={readOnly}
                        name='payedDateTom'
                        label={t('invoice:adminInvoiceTemplatePage.templateInformation.payedDateTom')}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={3}>
                    <Controller
                      name='memberType'
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          value={field.value ?? ''}
                          select
                          disabled={readOnly}
                          fullWidth
                          size='small'
                          sx={{ backgroundColor: 'white' }}
                          label={t('invoice:adminInvoiceTemplatePage.templateInformation.memberType')}
                        >
                          {memberTypeOptions.map((memberType) => (
                            <MenuItem key={memberType} value={memberType}>
                              {getMemberTypeText(memberType)}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                  </Grid>
                  {!usesRecurringSelection && canSeeInternalFields && (
                    <Grid item xs={12} sm={3}>
                      <ControlledDatePicker
                        disabled={readOnly}
                        name='resdate'
                        label={t('invoice:adminInvoiceTemplatePage.templateInformation.exitDate')}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
            {showAssociations && (
              <>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Apartment fontSize='small' sx={{ mr: 1 }} aria-hidden />
                  </Box>
                  <Typography variant='subtitle1' sx={{ fontWeight: 500, mb: 1 }}>
                    {t('invoice:adminInvoiceTemplatePage.templateInformation.notifyAssociations')}&nbsp;<strong>({associations.length})</strong>
                  </Typography>
                  {associations.length > 0 ? (
                    <ListDisplay items={associations} onDelete={readOnly ? undefined : (item) => handleAssociationDeleted(item as Associations)} />
                  ) : (
                    <ListDisplay items={[{ id: 0, name: t('invoice:adminInvoiceTemplatePage.templateInformation.allAssociations') }]} />
                  )}
                  {renderAddButton(() => setShowAddAssociations(true))}
                </Grid>
                <Dialog open={showAddAssociations} onClose={() => setShowAddAssociations(false)} maxWidth='sm' fullWidth>
                  <DialogTitle>{t('invoice:adminInvoiceTemplatePage.templateInformation.notifyAssociations')}</DialogTitle>
                  <DialogContent>
                    <InvoiceTemplateAddAssociations
                      selected={associations}
                      callback={handleAssociationsAdded}
                      closeEdit={() => setShowAddAssociations(false)}
                    />
                  </DialogContent>
                </Dialog>
              </>
            )}

            {renderEditableProfileList('profilesIncludeItems', <AddBox fontSize='small' sx={{ mr: 1 }} aria-hidden />)}
            {renderEditableProfileList('profilesExcludeItems', <IndeterminateCheckBox fontSize='small' sx={{ mr: 1 }} aria-hidden />)}
          </Box>
        </Grid>
      </Grid>

      <Grid container mb={1}>
        <Grid item xs={12}>
          <Typography sx={{ fontWeight: 500, color: 'text.secondary', mb: 1 }}>
            {t('invoice:adminInvoiceTemplatePage.templateInformation.tagging')}
          </Typography>
          <Box sx={{ mb: 2 }}>{renderEditableProfileList('profilesMarkItems', <BookmarkAdd fontSize='small' sx={{ mr: 1 }} aria-hidden />, 0)}</Box>
        </Grid>
      </Grid>
    </Grid>
  );
};
